// ✅ src/pages/Vehicle/VehicleTypeMasterListing.jsx
// Combined page: Vehicle Type Master form sits above the listing table.
// Visual design aligned with ProductionBOMListing.
//
// APIs:
//   GET  /api/typeMasterListing/list?search=&page=1&limit=10
//   POST /api/typeMasterListing/delete   { ids: [] }
//   GET  /api/typeMaster/:id
//   GET  /api/typeMaster/view?vehicleTypeId=
//   POST /api/typeMaster/save            { vehicleTypeId, vehicleTypeName }
//
// Edit fills the form on this page (no navigate to VehicleTypeMaster).
// Sub Type / Sub Assembly / Click still navigate to their pages.

import { useState, useContext, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaSearch,
  FaSave,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const LIST_API = "https://nissi-india.com/api/typeMasterListing";
const MASTER_API = "https://nissi-india.com/api/typeMaster";

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const VehicleTypeMasterListing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const formRef = useRef(null);
  const inputRef = useRef(null);

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [editSelectedIds, setEditSelectedIds] = useState([]);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState([]);

  const [vehicleTypeId, setVehicleTypeId] = useState(0);
  const [vehicleTypeName, setVehicleTypeName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const normalizeRow = (r) => {
    const id =
      r?.id ??
      r?.vehicleTypeId ??
      r?.vehicletypeid ??
      r?.fld_vehicletypeid ??
      r?.fld_vehicleTypeId ??
      0;

    return {
      ...r,
      id: Number(id) || 0,
      vehicleType:
        r?.vehicleType ??
        r?.vehicleTypeName ??
        r?.vehicletypename ??
        r?.fld_vehicletypename ??
        r?.["Vehicle Type"] ??
        "-",
      vehicleSubType:
        r?.vehicleSubType ??
        r?.vehiclesubtype ??
        r?.fld_vehicle_subtypename ??
        r?.["Vehicle Sub Type"] ??
        "-",
      subAssembly:
        r?.subAssembly ??
        r?.subassembly ??
        r?.fld_subassemblyname ??
        r?.["Vehicle Sub Assembly"] ??
        "-",
      click: r?.click ?? "Click",
    };
  };

  const fetchList = async (searchText = search, pageNo = page) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await axios.get(`${LIST_API}/list`, {
        params: { search: searchText, page: pageNo, limit },
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      const normalized = data.map(normalizeRow).filter((x) => x.id > 0);

      setRows(normalized);
      setTotalPages(res.data?.totalPages || 1);
      setTotal(res.data?.total || 0);

      const tp = res.data?.totalPages || 1;
      if (pageNo > tp) setPage(tp);

      setEditSelectedIds([]);
      setDeleteSelectedIds([]);

      if (normalized.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to fetch list!");
      setRows([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadEdit = async (id) => {
    const vid = toInt(id);
    if (vid <= 0) return;

    setLoadingEdit(true);
    try {
      try {
        const r1 = await axios.get(`${MASTER_API}/${vid}`, {
          headers: commonHeaders,
        });

        if (r1.data?.success) {
          const name =
            r1.data?.data?.vehicleTypeName ??
            r1.data?.data?.vehicleType ??
            r1.data?.data?.fld_vehicletypename ??
            r1.data?.vehicleTypeName ??
            "";
          setVehicleTypeName(String(name || ""));
          return;
        }
      } catch (_) {}

      const r2 = await axios.get(`${MASTER_API}/view`, {
        params: { vehicleTypeId: vid },
        headers: commonHeaders,
      });

      if (!r2.data?.success) {
        toast.error(r2.data?.message || "Failed to load edit data");
        return;
      }

      const name =
        r2.data?.data?.vehicleTypeName ??
        r2.data?.data?.vehicleType ??
        r2.data?.data?.fld_vehicletypename ??
        r2.data?.vehicleTypeName ??
        "";
      setVehicleTypeName(String(name || ""));
    } catch (err) {
      console.error(err);
      toast.error("Server error while loading edit data");
    } finally {
      setLoadingEdit(false);
    }
  };

  useEffect(() => {
    fetchList(search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, commonHeaders]);

  useEffect(() => {
    const idFromQs = toInt(
      searchParams.get("vehicleTypeId") ||
        searchParams.get("id") ||
        searchParams.get("vehicletypeid")
    );
    if (idFromQs > 0) {
      setVehicleTypeId(idFromQs);
      setEditSelectedIds([idFromQs]);
      loadEdit(idFromQs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, commonHeaders]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => inputRef.current?.focus(), 250);
  };

  const clearForm = () => {
    setVehicleTypeId(0);
    setVehicleTypeName("");
    setEditSelectedIds([]);
    if (searchParams.get("vehicleTypeId") || searchParams.get("id") || searchParams.get("vehicletypeid")) {
      navigate(window.location.pathname, { replace: true });
    }
  };

  const startAddNew = () => {
    clearForm();
    scrollToForm();
  };

  const startEdit = (id, nameFromRow = "") => {
    const vid = toInt(id);
    if (vid <= 0) {
      toast.error("Invalid selected id. Please refresh the page.");
      return;
    }
    setVehicleTypeId(vid);
    setEditSelectedIds([vid]);
    if (nameFromRow) setVehicleTypeName(String(nameFromRow));
    scrollToForm();
    loadEdit(vid);
  };

  const handleSave = async () => {
    if (!vehicleTypeName.trim()) {
      toast.error("Please enter vehicle type!");
      return;
    }

    setSaving(true);
    try {
      const r = await axios.post(
        `${MASTER_API}/save`,
        {
          vehicleTypeId: vehicleTypeId || 0,
          vehicleTypeName: vehicleTypeName.trim(),
        },
        { headers: commonHeaders }
      );

      if (!r.data?.success) {
        toast.error(r.data?.message || "Save failed");
        return;
      }

      toast.success(r.data?.message || "Saved successfully!");
      clearForm();
      setPage(1);
      await fetchList(search, 1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Server error while saving");
    } finally {
      setSaving(false);
    }
  };

  const toggleEditSelect = (id) => {
    setEditSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDeleteSelect = (id) => {
    setDeleteSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllEdit = () => {
    if (!rows.length) return;
    if (editSelectedIds.length === rows.length) setEditSelectedIds([]);
    else setEditSelectedIds(rows.map((r) => r.id));
  };

  const toggleSelectAllDelete = () => {
    if (!rows.length) return;
    if (deleteSelectedIds.length === rows.length) setDeleteSelectedIds([]);
    else setDeleteSelectedIds(rows.map((r) => r.id));
  };

  const handleDeleteSelected = async () => {
    if (!deleteSelectedIds.length) {
      toast.warn("Please select at least one record in Delete checkbox!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${deleteSelectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        `${LIST_API}/delete`,
        { ids: deleteSelectedIds },
        { headers: commonHeaders }
      );
      toast.success(res.data?.message || "Deleted successfully");
      if (deleteSelectedIds.includes(vehicleTypeId)) clearForm();
      fetchList(search, page);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed!");
    }
  };

  const handleEditSelected = () => {
    if (!editSelectedIds.length) {
      toast.warn("Please select one record in Edit checkbox!");
      return;
    }
    if (editSelectedIds.length > 1) {
      toast.warn("Please select only one record to edit!");
      return;
    }

    const id = toInt(editSelectedIds[0]);
    const row = rows.find((r) => r.id === id);
    startEdit(id, row?.vehicleType || "");
  };

  const handleSearchClick = () => {
    setPage(1);
    fetchList(search, 1);
  };

  const handleClear = () => {
    setSearch("");
    setPage(1);
    fetchList("", 1);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYPE MASTER LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Type Master
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
              {vehicleTypeId ? (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-amber-900 text-amber-100" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  Editing ID: {vehicleTypeId}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== ADD / EDIT FORM ==================== */}
          <div
            ref={formRef}
            className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                {vehicleTypeId ? "Edit Vehicle Type" : "Add Vehicle Type"}
              </h3>

              <div className="flex gap-2">
                {vehicleTypeId ? (
                  <button
                    type="button"
                    onClick={clearForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setVehicleTypeName("")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loadingEdit || !vehicleTypeName.trim()}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving || loadingEdit || !vehicleTypeName.trim()
                      ? "opacity-60 cursor-not-allowed bg-gray-400"
                      : `${buttonClass} ${hoverClass}`
                  } text-white`}
                >
                  <FaSave />
                  {saving ? "Saving..." : vehicleTypeId ? "Update" : "Save"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Vehicle Type</label>
                <input
                  ref={inputRef}
                  type="text"
                  value={vehicleTypeName}
                  onChange={(e) => setVehicleTypeName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  placeholder={loadingEdit ? "Loading..." : "Enter vehicle type..."}
                  disabled={loadingEdit}
                  className={fieldClass}
                />
              </div>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClear}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearchClick}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Search</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearchClick();
                    }}
                    placeholder="Type to search..."
                    className={fieldClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== SELECTION BAR ==================== */}
          {(editSelectedIds.length > 0 || deleteSelectedIds.length > 0) && (
            <div
              className={`rounded-lg p-4 mb-6 ${
                isDark
                  ? "bg-blue-900/30 border border-blue-700"
                  : "bg-blue-50 border border-blue-200"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-4">
                  <span className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                    Edit Selected: {editSelectedIds.length}
                  </span>
                  <span className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                    Delete Selected: {deleteSelectedIds.length}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleEditSelected}
                    className={`px-4 py-2 text-white rounded-lg font-medium ${buttonClass} ${hoverClass}`}
                  >
                    Edit
                  </button>

                  <button
                    onClick={handleDeleteSelected}
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium bg-red-600 hover:bg-red-700"
                  >
                    <FaTrash className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && editSelectedIds.length === rows.length}
                        onChange={toggleSelectAllEdit}
                        className="mr-2 w-4 h-4 cursor-pointer"
                      />
                      Edit
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && deleteSelectedIds.length === rows.length}
                        onChange={toggleSelectAllDelete}
                        className="mr-2 w-4 h-4 cursor-pointer"
                      />
                      Delete
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Sub Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Sub Assembly</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Click</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {rows.map((r, idx) => {
                    const isEditingRow = vehicleTypeId && r.id === vehicleTypeId;

                    return (
                      <tr
                        key={r.id}
                        className={`${
                          isEditingRow
                            ? isDark
                              ? "bg-amber-900/30"
                              : "bg-amber-50"
                            : idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={editSelectedIds.includes(r.id)}
                            onChange={() => toggleEditSelect(r.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>

                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={deleteSelectedIds.includes(r.id)}
                            onChange={() => toggleDeleteSelect(r.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>

                        <td className="px-6 py-4">
                          <button
                            type="button"
                            onClick={() => startEdit(r.id, r.vehicleType)}
                            className={`font-medium text-left ${
                              isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
                            }`}
                            title="Edit this type"
                          >
                            {r.vehicleType}
                          </button>
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={() =>
                              navigate(`/vehicle/VehicleSubTypeMaster?vehicletypeid=${r.id}`)
                            }
                            className={`font-medium hover:underline ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {r.vehicleSubType}
                          </button>
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={() =>
                              navigate(`/vehicle/VehicleTypeSubAssemblyMaster?vehicletypeid=${r.id}`)
                            }
                            className={`font-medium hover:underline ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {r.subAssembly}
                          </button>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => navigate(`/vehicle/VehicleSubAssembly?vehicletypeid=${r.id}`)}
                            className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            {r.click}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <span className="font-semibold">{total === 0 ? 0 : (page - 1) * limit + 1}</span>{" "}
              to <span className="font-semibold">{Math.min(page * limit, total)}</span> of{" "}
              <span className="font-semibold">{total}</span> results
            </div>

            {!loading && total > 0 && totalPages > 1 && (
              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 font-sans">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-medium ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      page === i + 1
                        ? `${buttonClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-medium ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMasterListing;